/**
 * Everything the backend knows about an exercise plan: listing a client's,
 * reading one whole, writing one, sending it, and rendering it as a document.
 *
 * All of it bar deleting, which has no endpoint — a plan removed in the
 * browser is hidden there rather than deleted, which is the whole of what
 * `exercisePlanStore` is left holding.
 *
 * Everything snake_case stops at `exerciseWire`, which this is the only caller
 * of — the rest of the module sees `ExercisePlanSummary`, `ExerciseEntry` and
 * the app's own casing. The conversions sit there rather than here so the
 * suites can run their fixtures through them while this module is mocked.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { PaginatedResponse } from "../../../lib/api";
import {
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
  toExercisePayloads,
  toExercisePlanRecord,
  toExercisePlanSummary,
} from "../exerciseWire";
import type { GetToken } from "../../auth/types/auth.types";
import type {
  ExerciseEntry,
  ExercisePlanDetailResponse,
  ExercisePlanRecord,
  ExercisePlanSummaryPage,
  ExercisePlanSummaryResponse,
  SaveExercisePlanPayload,
  SendExercisePlanPayload,
  SendExercisePlanResponse,
} from "../types/exercisePlans.types";

/**
 * How long the document call is given. A minute, matching the meal plan's:
 * rendering is work, and the client's default is written for calls that read
 * a row back.
 */
const PDF_TIMEOUT_MS = 60_000;

// Re-exported so a call site reads one module rather than two — the page size
// is about this endpoint, whichever file the number is written in.
export { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE };

export interface FetchExercisePlansOptions {
  /** A client's `public_id`. Absent asks for the whole workspace. */
  client?: string;
  /** 1-based, the way the endpoint counts. */
  page?: number;
  pageSize?: number;
  signal?: AbortSignal;
}

/** One page of a client's exercise plans. */
export async function fetchExercisePlans(
  getToken: GetToken,
  {
    client,
    page,
    pageSize = DEFAULT_PAGE_SIZE,
    signal,
  }: FetchExercisePlansOptions = {}
): Promise<ExercisePlanSummaryPage> {
  const token = await getToken();
  if (!token) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error("No active Clerk session - cannot load exercise plans.");
  }

  const response = await api.get<
    PaginatedResponse<ExercisePlanSummaryResponse>
  >(API_ENDPOINTS.exercisePlans.list, {
    token,
    // `undefined` entries are dropped by the API client, so an unfiltered ask
    // never puts an empty `client=` in the URL.
    query: {
      client: client || undefined,
      page,
      page_size: Math.min(pageSize, MAX_PAGE_SIZE),
    },
    signal,
  });

  return {
    count: response?.count ?? 0,
    hasNext: !!response?.next,
    plans: (response?.results ?? []).map(toExercisePlanSummary),
  };
}

/**
 * One plan, by `public_id` — the only place the exercises on a plan come
 * from, which is what the view dialog opens on.
 */
export async function fetchExercisePlan(
  getToken: GetToken,
  publicId: string,
  signal?: AbortSignal
): Promise<ExercisePlanRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot load the exercise plan.");
  }

  const detail = await api.get<ExercisePlanDetailResponse>(
    API_ENDPOINTS.exercisePlans.detail(publicId),
    { token, signal }
  );

  return toExercisePlanRecord(detail);
}

export interface SaveExercisePlanInput {
  /** The client's `public_id`. Sent on the update as well as the create. */
  clientId: string;
  /** The lines as the dialog holds them — converted on the way out. */
  entries: ExerciseEntry[];
}

/**
 * Writes a new plan, and hands back what the backend made of it.
 *
 * The response is the whole plan — its `public_id`, the name and number the
 * backend gave it, and the exercises as stored — so a create is also a read,
 * and nothing has to be fetched again to show what was just written.
 */
export async function createExercisePlan(
  getToken: GetToken,
  { clientId, entries }: SaveExercisePlanInput
): Promise<ExercisePlanRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot create an exercise plan.");
  }

  const body: SaveExercisePlanPayload = {
    client: clientId,
    exercises: toExercisePayloads(entries),
  };

  const detail = await api.post<ExercisePlanDetailResponse>(
    API_ENDPOINTS.exercisePlans.list,
    body,
    { token }
  );

  return toExercisePlanRecord(detail);
}

/**
 * Rewrites one, and hands back what it now holds.
 *
 * A replace rather than a patch — the plan ends up holding exactly what went
 * up, deleted lines included — which is what makes the dialog's Save mean what
 * it looks like it means. The client travels with it, the same way the create
 * sends one.
 */
export async function updateExercisePlan(
  getToken: GetToken,
  publicId: string,
  { clientId, entries }: SaveExercisePlanInput
): Promise<ExercisePlanRecord> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot save the exercise plan.");
  }

  const body: SaveExercisePlanPayload = {
    client: clientId,
    exercises: toExercisePayloads(entries),
  };

  const detail = await api.put<ExercisePlanDetailResponse>(
    API_ENDPOINTS.exercisePlans.detail(publicId),
    body,
    { token }
  );

  return toExercisePlanRecord(detail);
}

/**
 * Sends the plan to the addresses given, and hands back whatever the backend
 * had to say about it.
 *
 * The message is the backend’s own wording, so a change there reaches the
 * dietitian without this module being touched. An answer with none is not a
 * failure — the call succeeding is what says the plan went.
 */
export async function sendExercisePlan(
  getToken: GetToken,
  publicId: string,
  emails: string[]
): Promise<string | undefined> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot send the exercise plan.");
  }

  const body: SendExercisePlanPayload = { emails };

  const response = await api.post<SendExercisePlanResponse>(
    API_ENDPOINTS.exercisePlans.send(publicId),
    body,
    { token }
  );

  return response?.message?.trim() || undefined;
}

/**
 * The plan as a document, for the dietitian's own copy.
 *
 * The bytes rather than a link to them, so the caller hands the file straight
 * to the browser — see `saveExercisePlanPdf`. The backend renders it because
 * it is the one that knows the dietitian's brand, and so that the document a
 * client is emailed and the one their dietitian downloads are the same
 * document.
 *
 * A longer timeout than the rest of the module: rendering a plan is work, and
 * the default is written for calls that only read a row back.
 */
export async function fetchExercisePlanPdf(
  getToken: GetToken,
  publicId: string,
  signal?: AbortSignal
): Promise<Blob> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot download a plan.");
  }

  return api.getBlob(API_ENDPOINTS.exercisePlans.pdf(publicId), {
    token,
    signal,
    // A PDF for preference, and anything rather than nothing. `application/pdf`
    // alone is refused — see `mealPlans.pdf`, where the same wildcard is
    // explained at length.
    headers: { Accept: "application/pdf, */*" },
    timeoutMs: PDF_TIMEOUT_MS,
  });
}
